import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material'
import {
  Menu as MenuIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  AdminPanelSettings,
  Palette as PaletteIcon,
  Receipt as ReceiptIcon,
  Storage as StorageIcon,
  People as PeopleIcon,
  MenuBook as MenuBookIcon,
  Feedback as FeedbackIcon,
  HomeOutlined as OverviewIcon,
  AccountTreeOutlined as ProjectsIcon,
} from '@mui/icons-material'
import { NavigationProps } from '../types'
import ProductTitle from './ProductTitle'
import { getDocsSiteOrigin } from '../config'
import { useAuth } from '../contexts/AuthContext'
import { useRoleAssumption } from '../contexts/RoleAssumptionContext'

interface MenuItem {
  text: string
  path: string
  icon: React.ReactNode
  disabled?: boolean
}

const Navigation: React.FC<NavigationProps> = () => {
  const [mobileOpen, setMobileOpen] = useState<boolean>(false)
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { user, isAuthenticated, logout } = useAuth()
  const { isRoleAssumed, assumedUser } = useRoleAssumption()

  const menuItems: MenuItem[] = []

  // Only add menu items if user is authenticated
  if (isAuthenticated) {
    // Billing remains visible only to joram in dashboard navigation.
    if (user?.login === 'joram') {
      menuItems.push({
        text: 'Billing',
        path: '/billing',
        icon: <ReceiptIcon />
      })
    }

  }

  const handleDrawerToggle = (): void => {
    setMobileOpen(!mobileOpen)
  }

  const handleNavigation = (path: string): void => {
    navigate(path)
    if (isMobile) {
      setMobileOpen(false)
    }
  }

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setUserMenuAnchor(event.currentTarget)
  }

  const handleUserMenuClose = (): void => {
    setUserMenuAnchor(null)
  }

  const handleLogout = (): void => {
    logout()
    handleUserMenuClose()
    navigate('/login')
  }

  const isActive = (path: string): boolean => {
    // Handle repositories tab matching
    if (path.includes('?tab=2')) {
      return location.pathname + location.search === path
    }
    return location.pathname === path
  }

  const drawer = (
    <Box sx={{ width: 250, p: 2 }}>
      <Box 
        onClick={() => {
          navigate('/')
          handleDrawerToggle()
        }}
        sx={{ 
          mb: 2,
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8
          }
        }}
      >
        <ProductTitle 
          size="medium" 
          centered={false}
        />
      </Box>
      {isAuthenticated && (
        <List sx={{ mb: 1 }}>
          {[
            { text: 'Home', path: '/', icon: <OverviewIcon /> },
            { text: 'Projects', path: '/projects', icon: <ProjectsIcon /> },
            { text: 'Databases', path: '/databases', icon: <StorageIcon /> },
          ].map((item) => (
            <ListItem
              key={item.text}
              onClick={() => handleNavigation(item.path)}
              sx={{ cursor: 'pointer', borderRadius: 1 }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          ))}
          {[
            { text: 'Users', path: '/databases/users', icon: <PeopleIcon /> },
          ].map((item) => (
            <ListItem
              key={item.text}
              onClick={() => handleNavigation(item.path)}
              sx={{ cursor: 'pointer', borderRadius: 1 }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          ))}
        </List>
      )}
      <List>
        {menuItems.map((item) => (
          <ListItem
            key={item.text}
            onClick={() => !item.disabled && handleNavigation(item.path)}
            sx={{
              bgcolor: isActive(item.path) ? 'var(--palette-primary-100)' : 'transparent',
              color: isActive(item.path) ? 'white' : item.disabled ? 'text.disabled' : 'inherit',
              cursor: item.disabled ? 'default' : 'pointer',
              opacity: item.disabled ? 0.5 : 1,
              '&:hover': {
                bgcolor: item.disabled ? 'transparent' : (isActive(item.path) ? 'var(--palette-primary-200)' : 'action.hover'),
              },
            }}
          >
            <ListItemIcon sx={{ color: isActive(item.path) ? 'white' : item.disabled ? 'text.disabled' : 'inherit' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
    </Box>
  )

  return (
    <>
      {isRoleAssumed && (
        <Box
          sx={{
            bgcolor: '#8B0000', // Dark red
            color: 'white',
            py: 1,
            px: 2,
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: '1.1rem',
          }}
        >
          ⚠️ WARNING: ROLE ASSUMED - Acting as {assumedUser?.github_username} (ID: {assumedUser?.id})
        </Box>
      )}
      {/* Mobile-only header: menu + user (desktop uses sidebar with logo + user menu) */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <AppBar
          position="static"
          sx={{
            bgcolor: isRoleAssumed ? '#8B0000' : undefined,
          }}
        >
          <Toolbar sx={{ px: 2, minHeight: 48 }}>
            <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
            <Box sx={{ flexGrow: 1 }} />
            {/* User menu (mobile) */}
            {isAuthenticated && user && (
              <>
                <IconButton
                  color="inherit"
                  aria-label="user-profile"
                  onClick={handleUserMenuOpen}
                  sx={{ ml: 2 }}
                >
                  {user.avatar_url ? (
                    <Avatar 
                      src={user.avatar_url} 
                      alt={user.name || user.login}
                      sx={{ width: 32, height: 32 }}
                    />
                  ) : (
                    <AccountIcon />
                  )}
                </IconButton>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem disabled>
                    <Typography variant="body2">
                      {user.name || user.login}
                    </Typography>
                  </MenuItem>
                  {user.login === 'joram' && (
                    <MenuItem
                      onClick={() => {
                        handleUserMenuClose()
                        window.open('https://admin.veilstream.com', '_blank', 'noopener,noreferrer')
                      }}
                    >
                      <ListItemIcon>
                        <AdminPanelSettings fontSize="small" />
                      </ListItemIcon>
                      Admin
                    </MenuItem>
                  )}
                  <MenuItem onClick={() => {
                    handleUserMenuClose()
                    navigate('/settings')
                  }}>
                    <ListItemIcon>
                      <PaletteIcon fontSize="small" />
                    </ListItemIcon>
                    Settings
                  </MenuItem>
                  {user.login === 'joram' && (
                    <MenuItem onClick={() => {
                      handleUserMenuClose()
                      navigate('/billing')
                    }}>
                      <ListItemIcon>
                        <ReceiptIcon fontSize="small" />
                      </ListItemIcon>
                      Billing
                    </MenuItem>
                  )}
                  <MenuItem 
                    component="a"
                    href={getDocsSiteOrigin()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleUserMenuClose}
                  >
                    <ListItemIcon>
                      <MenuBookIcon fontSize="small" />
                    </ListItemIcon>
                    Documentation
                  </MenuItem>
                  <MenuItem onClick={() => {
                    handleUserMenuClose()
                    navigate('/feedback')
                  }}>
                    <ListItemIcon>
                      <FeedbackIcon fontSize="small" />
                    </ListItemIcon>
                    Feedback
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    Logout
                  </MenuItem>
                </Menu>
              </>
            )}
          </Toolbar>
        </AppBar>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>
    </>
  )
}

export default Navigation
